import { createGlobalState } from '@vueuse/core';
import { ref, nextTick } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_NOT_FOUND } from '~/support/HttpStatuses';
import adminTeamsClient from '~/services/adminTeamsClient';
import {
    groupsClient,
    organizationsClient,
    phoneNumbersClient,
    teamCustomAttributesClient,
    usersClient,
    invitationsClient,
    adminUsersClient,
} from '~/services';

export const useTeamDetails = createGlobalState(() => {
    const toasted = useToasted();

    const team = ref({});
    const loading = ref(true);

    const catchRequestError = (error) => {
        console.error(error);
        if (
            !error?.response?.status ||
            error?.response?.status >= HTTP_INTERNAL_SERVER_ERROR ||
            error?.response.status == HTTP_NOT_FOUND
        ) {
            toasted?.global.platform_error();
        } else {
            toasted?.error(error.response?.data?.message || 'Something went wrong.');
        }
    };

    let onMountedTeamCallback = () => {};
    const onMountedTeam = (callback) => {
        onMountedTeamCallback = callback;
    };

    const mount = async (teamId) => {
        await nextTick();

        if (!teamId) {
            team.value = {};
            loading.value = false;
            onMountedTeamCallback?.();
            return;
        }

        try {
            team.value.id = teamId;
            loading.value = true;

            const response = await adminTeamsClient.showTeam(teamId);
            team.value = response.data;
            loading.value = false;
            onMountedTeamCallback?.();
        } catch (e) {
            catchRequestError(e);
        }
    };

    const unmount = () => {
        team.value = {};
        loading.value = false;
    };

    const parent = ref(undefined);
    const parentLoading = ref(false);
    const getParent = async () => {
        if (!team.value.parent_id) {
            return;
        }

        try {
            parentLoading.value = true;
            const response = await adminTeamsClient.showTeam(team.value.parent_id);
            parent.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            parentLoading.value = false;
        }
    };

    const owner = ref(undefined);
    const ownerLoading = ref(false);
    const getOwner = async () => {
        try {
            ownerLoading.value = true;
            const response = await adminUsersClient.showUser(team.value.owner_id);
            owner.value = response.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            ownerLoading.value = false;
        }
    };

    const invitationsCount = ref(undefined);
    const invitationsCountLoading = ref(false);
    const getInvitationsCount = async () => {
        try {
            invitationsCountLoading.value = true;
            const response = await invitationsClient.invitesCount(team.value.id);
            invitationsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            invitationsCountLoading.value = false;
        }
    };

    const usersCount = ref(undefined);
    const usersCountLoading = ref(false);
    const getUsersCount = async () => {
        try {
            usersCountLoading.value = true;
            const response = await usersClient.usersCount(team.value.id);
            usersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            usersCountLoading.value = false;
        }
    };

    const teamsCount = ref(undefined);
    const teamsCountLoading = ref(false);
    const getTeamsCount = async () => {
        try {
            teamsCountLoading.value = true;
            const response = await organizationsClient.countTeams(team.value.id);
            teamsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            teamsCountLoading.value = false;
        }
    };

    const groupsCount = ref(undefined);
    const groupsCountLoading = ref(false);
    const getGroupsCount = async () => {
        try {
            groupsCountLoading.value = true;
            const response = await groupsClient.countGroups(team.value.id);
            groupsCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            groupsCountLoading.value = false;
        }
    };

    const phoneNumbersCount = ref(undefined);
    const phoneNumbersCountLoading = ref(false);
    const getPhoneNumbersCount = async () => {
        try {
            phoneNumbersCountLoading.value = true;
            const response = await phoneNumbersClient.countPhoneNumbers(team.value.id);
            phoneNumbersCount.value = response.data.count;
        } catch (e) {
            catchRequestError(e);
        } finally {
            phoneNumbersCountLoading.value = false;
        }
    };

    const phoneNumbers = ref(undefined);
    const phoneNumbersLoading = ref(false);
    const getPhoneNumbers = async (params) => {
        try {
            phoneNumbersLoading.value = true;
            const response = await phoneNumbersClient.listPhoneNumbers(team.value.id, params);
            phoneNumbers.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            phoneNumbersLoading.value = false;
        }
    };

    const customAttributes = ref(undefined);
    const customAttributesLoading = ref(false);
    const getCustomAttributes = async () => {
        try {
            customAttributesLoading.value = true;
            const response = await teamCustomAttributesClient.getList(team.value.id);
            customAttributes.value = response.data.data;
        } catch (e) {
            catchRequestError(e);
        } finally {
            customAttributesLoading.value = false;
        }
    };

    return {
        loading,
        team,
        mount,
        unmount,
        onMountedTeam,

        owner,
        ownerLoading,
        getOwner,

        parent,
        parentLoading,
        getParent,

        invitationsCount,
        invitationsCountLoading,
        getInvitationsCount,

        usersCount,
        usersCountLoading,
        getUsersCount,

        teamsCount,
        teamsCountLoading,
        getTeamsCount,

        groupsCount,
        groupsCountLoading,
        getGroupsCount,

        phoneNumbersCount,
        phoneNumbersCountLoading,
        getPhoneNumbersCount,

        phoneNumbers,
        phoneNumbersLoading,
        getPhoneNumbers,

        customAttributes,
        customAttributesLoading,
        getCustomAttributes,
    };
});
